import {
    clearKeyword,
    clearSelection,
    setKeyword,
    setPage,
    setPageSize,
} from '../reducers';

import {
    selectionSelector,
    selectVisibility,
    visibilityStateSelector,
} from '../selectors';

import {columns} from '../tables/columns';
import {
    useAppDispatch,
    useAppSelector
} from '../../../../hooks/useTypedReactRedux';

import {
    useAccountApplicationsQuery,
    useDeleteAccountApplicationMutation,
} from './useAccountApplicationsQuery';

// FNP-946: records come from React Query; selection, paging and keyword stay in
// Redux. Return shape unchanged so the table, grid, buttons, inputs and the
// DASHBOARD consuming this hook did not change.
//
// The dashboard (src/features/dashboard/hooks/useDashboard.tsx) reads records
// through this hook with the signed-in user's account id - that is why
// accountId is part of the query key rather than a client-side filter.
export const useAccountApplications = (accountId?: string) => {

    const dispatch = useAppDispatch();

    const selection = useAppSelector((state) => selectionSelector(state));
    const visibility = useAppSelector((state) => visibilityStateSelector(state));

    const {
        data,
        isLoading,
        refetch
    } = useAccountApplicationsQuery(accountId);

    const deleteMutation = useDeleteAccountApplicationMutation();

    const {
        items,
        visibleItems,
        limit,
        keyword,
        page,
        totalPages,
        visibleCount,
    } = selectVisibility(visibility, data ?? []);

    // FETCH
    const fetchRecords = () => {
        refetch();
    };

    const deleteRecords = () => {
        if (Object.keys(selection).length > 0) {
            for (const key in selection) {
                deleteMutation.mutate(key);
            }
            // Clear the checkboxes once the deletions are issued - otherwise the
            // selection keeps naming records that no longer exist.
            dispatch(clearSelection());
        }
    };


    // FILTERING AND PAGING
    const handleFilterByKeyword = (keyword: string) => {
        if (keyword !== '') {
            dispatch(setKeyword(keyword));
        } else {
            dispatch(clearKeyword());
        }
    };

    const handleItemsPerPageChange = (pageSize: string | null) => {
        dispatch(setPageSize(pageSize));
    };

    const handlePageChange = (page: number) => {
        dispatch(setPage(page));
    };

    // BATCH ACTIONS
    const handleBatchActionCancel = () => {
        dispatch(clearSelection());
    };

    return {
        items,
        visibleItems,
        limit,
        loading: isLoading,
        keyword,
        page,
        totalPages,
        visibleCount,
        columns,
        fetchRecords,
        deleteRecords,
        handleFilterByKeyword,
        handleItemsPerPageChange,
        handleBatchActionCancel,
        handlePageChange,
    };
};
